import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Bowser from 'bowser';
import React from 'react';
import { homeStyles } from '.';
import logoCallActivity from '../../assets/logo-activity-call.png';
import logoProdCast from '../../assets/logo-prodcast-call.png';
import logoCallWednesday from '../../assets/logo-twc-call.png';
import logoYoutube from '../../assets/logo-youtube.png';
import { getCallStatus } from './call-status';
import { ShareCallButton } from './share-call-button';

// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
type IntervalFunction = () => unknown | void;
function useInterval(callback: IntervalFunction, delay: number) {
  const savedCallback = React.useRef<IntervalFunction | null>(null);

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      if (savedCallback.current !== null) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export interface ConferenceCall {
  day: number;
  name: string;
  dateDay: string;
  dateTime: string;
  phone: string;
  code: string;
  youtube: string;
  logo: string;
  maxWidth: number;
}

export const conferenceCalls: ConferenceCall[] = [
  {
    day: 3,
    name: 'TWC',
    dateDay: 'Wednesday',
    dateTime: '@ 12:30 PM ET',
    phone: '312-626-6799',
    code: '969 606 853#',
    youtube: 'https://www.youtube.com/theallianceyt',
    logo: logoCallWednesday,
    maxWidth: 100,
  },
  {
    day: 4,
    name: 'Product Call',
    dateDay: 'Thursday',
    dateTime: '@ 1:00 PM ET',
    phone: '253-215-8782',
    code: '875 5255 6016',
    youtube:
      'https://us02web.zoom.us/j/85370582123?pwd=UzFydkcyVmhoWjNlSmxxREhaR3U4QT09',
    logo: logoProdCast,
    maxWidth: 60,
  },
  {
    day: 5,
    name: 'Activity Call',
    dateDay: 'Friday',
    dateTime: '@ 10:00 AM ET',
    phone: '312-626-6799',
    code: '969 606 853#',
    youtube: 'https://www.youtube.com/theallianceyt',
    logo: logoCallActivity,
    maxWidth: 60,
  },
  {
    day: 0,
    name: 'The Alliance Youtube Channel',
    dateDay: 'Friday',
    dateTime: '@ 10:00 AM ET',
    phone: '',
    code: '',
    youtube: 'https://www.youtube.com/theallianceyt',
    logo: logoYoutube,
    maxWidth: 200,
  },
];

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

export function MobileLiveBar() {
  // Hooks
  const isMobile = detectMobile();
  // State
  const [isLive, setIsLive] = React.useState(0);

  React.useEffect(() => {
    setIsLive(getCallStatus());
    // ignore 'getStatus' missing dependency array warning
    // eslint-disable-next-line
  }, []);

  // 10 second delay between intervals
  const delay = 10 * 1000;
  useInterval(() => {
    setIsLive(getCallStatus());
  }, delay);

  const call =
    conferenceCalls.find((i) => i.day === isLive) || conferenceCalls[3];
  return (
    <AppBar
      sx={{
        display: isLive !== 0 && isMobile ? 'flex' : 'none',
        background: `linear-gradient(90deg, #CE0E2D 0%, #FF257C 100%);`,
        position: 'relative',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-evenly"
        sx={{ py: 1, px: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}
      >
        <Box
          sx={{
            fontSize: 16,
            letterSpacing: 5,
            fontWeight: 700,
            textTransform: 'uppercase',
            fontFamily: 'Bebas Neue',
          }}
        >
          {call.name} LIVE
        </Box>
        <Box
          p={1}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
          }}
        >
          <Button
            aria-label={call.name}
            fullWidth
            size="small"
            sx={{ ...homeStyles.button }}
            href={call.youtube || ''}
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch Now
          </Button>
          <ShareCallButton call={call} />
        </Box>
      </Box>
    </AppBar>
  );
}
